// Samen (fase 2) on the device: the shared shopping list, recipes, the week menu, notes, and what may be seen of
// the other one's time. All of it works offline and arrives on the other phone through the sync.

import { useLiveQuery } from "dexie-react-hooks";
import { uuidv7 } from "@shared/uuid";
import {
  db,
  getMeta,
  setMeta,
  type Availability,
  type Meal,
  type Note,
  type Product,
  type Recipe,
  type SharedTodo,
  type ShopCategory,
  type ShoppingItem,
} from "./db";
import { addDays } from "@shared/time";
export { mergeIngredients } from "@shared/meals";
import type { Me } from "./session";
import { sync } from "./sync";

const now = () => new Date().toISOString();
const me = () => getMeta<Me>("me");
const live = <T extends { deleted_at: string | null }>(rows: T[]) => rows.filter((r) => !r.deleted_at);

// ---- Boodschappen ----

export function useShopCategories(): ShopCategory[] | undefined {
  return useLiveQuery(async () => live(await db.shop_categories.toArray()).sort((a, b) => a.position - b.position));
}

export function useShoppingItems(): ShoppingItem[] | undefined {
  return useLiveQuery(async () => live(await db.shopping_items.toArray()).sort((a, b) => (a.created_at < b.created_at ? -1 : 1)));
}

/** Products used before, most used first: suggestions while typing. */
export function useProducts() {
  return useLiveQuery(async () => live(await db.products.toArray()).sort((a, b) => b.use_count - a.use_count));
}

export async function addShopItem(
  name: string,
  options: { amount?: string | null; categoryId?: string | null; recipeId?: string | null } = {},
): Promise<void> {
  const id = uuidv7();
  const at = now();
  const user = await me();
  const product = live(await db.products.toArray()).find((p) => p.name.toLowerCase() === name.trim().toLowerCase());
  await sync.mutate(
    "shop.add",
    {
      id,
      name: name.trim(),
      amount: options.amount ?? null,
      categoryId: options.categoryId ?? null,
      recipeId: options.recipeId ?? null,
      at,
    },
    [id],
    async () => {
      await db.shopping_items.add({
        id,
        space_id: user?.settings?.shared_space_id ?? "",
        name: name.trim(),
        amount: options.amount ?? null,
        category_id: options.categoryId ?? product?.category_id ?? null,
        product_id: product?.id ?? null,
        recipe_id: options.recipeId ?? null,
        checked_at: null,
        version: 0,
        deleted_at: null,
        created_at: at,
        updated_at: at,
      });
    },
  );
}

export async function checkShopItem(item: ShoppingItem, checked: boolean): Promise<void> {
  const at = now();
  await sync.mutate("shop.check", { id: item.id, checked, at }, [item.id], async () => {
    await db.shopping_items.update(item.id, { checked_at: checked ? at : null });
  });
}

export async function removeShopItem(item: ShoppingItem): Promise<void> {
  const at = now();
  await sync.mutate("shop.remove", { id: item.id, at }, [item.id], async () => {
    await db.shopping_items.update(item.id, { deleted_at: at });
  });
}

export async function clearChecked(): Promise<void> {
  const at = now();
  const checked = live(await db.shopping_items.toArray()).filter((i) => i.checked_at);
  await sync.mutate(
    "shop.clearChecked",
    { at },
    checked.map((i) => i.id),
    async () => {
      for (const i of checked) await db.shopping_items.update(i.id, { deleted_at: at });
    },
  );
}

export async function setShopCategory(item: ShoppingItem, categoryId: string): Promise<void> {
  await sync.mutate("shop.setCategory", { id: item.id, categoryId }, [item.id], async () => {
    await db.shopping_items.update(item.id, { category_id: categoryId });
    if (item.product_id) await db.products.update(item.product_id, { category_id: categoryId });
  });
}

// ---- Vaste boodschappen (golf 9) ----

export async function setStaple(product: Pick<Product, "id">, on: boolean): Promise<void> {
  await sync.mutate("product.setStaple", { id: product.id, on }, [product.id], async () => {
    await db.products.update(product.id, { is_staple: on });
  });
}

/** The staples that are not on the list yet (open), by name. */
export function useMissingStaples(): Product[] | undefined {
  return useLiveQuery(async () => {
    const onList = new Set(
      live(await db.shopping_items.toArray())
        .filter((i) => !i.checked_at)
        .map((i) => i.name.toLowerCase()),
    );
    return live(await db.products.toArray())
      .filter((p) => p.is_staple && !onList.has(p.name.toLowerCase()))
      .sort((a, b) => a.name.localeCompare(b.name));
  });
}

// ---- Recepten ----

export function useRecipes(): Recipe[] | undefined {
  return useLiveQuery(async () => live(await db.recipes.toArray()).sort((a, b) => a.name.localeCompare(b.name)));
}

export type RecipeForm = Pick<Recipe, "name" | "minutes" | "servings" | "ingredients" | "steps" | "notes" | "source">;

export async function saveRecipe(id: string | null, form: RecipeForm): Promise<string> {
  const recipeId = id ?? uuidv7();
  const at = now();
  const user = await me();
  await sync.mutate("recipe.save", { id: recipeId, ...form }, [recipeId], async () => {
    if (id) await db.recipes.update(id, form);
    else
      await db.recipes.add({
        id: recipeId,
        space_id: user?.settings?.shared_space_id ?? "",
        ...form,
        version: 0,
        deleted_at: null,
        created_at: at,
        updated_at: at,
      });
  });
  return recipeId;
}

export async function removeRecipe(id: string): Promise<void> {
  const at = now();
  await sync.mutate("recipe.remove", { id, at }, [id], async () => {
    await db.recipes.update(id, { deleted_at: at });
  });
}

/** The ingredients that are not in the house yet, onto the shopping list. */
export async function recipeToShopping(recipe: Pick<Recipe, "id" | "ingredients">, names: string[]): Promise<number> {
  const wanted = recipe.ingredients.filter((i) => names.includes(i.name));
  for (const i of wanted) await addShopItem(i.name, { amount: i.amount, recipeId: recipe.id });
  return wanted.length;
}

/** The recipes on the menu from a day on, for the coming 7 days. */
export async function weekRecipes(from: string): Promise<Recipe[]> {
  const to = addDays(from, 6);
  const meals = live(await db.meals.where("meal_date").between(from, to, true, true).toArray());
  const ids = new Set(meals.filter((m) => m.recipe_id && m.place !== "out").map((m) => m.recipe_id!));
  return live(await db.recipes.bulkGet([...ids]).then((rs) => rs.filter((r): r is Recipe => !!r)));
}

// ---- Weekmenu ----

export async function setMenu(
  date: string,
  menu: { dish: string | null; recipeId: string | null; cookUserId: string | null; cookPersonId: string | null },
): Promise<void> {
  const at = now();
  const user = await me();
  const meal = live(await db.meals.where("meal_date").equals(date).toArray())[0] as Meal | undefined;
  const id = meal?.id ?? uuidv7();
  const values = {
    dish: menu.dish,
    recipe_id: menu.recipeId,
    cook_user_id: menu.cookUserId,
    cook_person_id: menu.cookPersonId,
    edited_at: at,
    // A dish of the other one at the table is a proposal until the meal's owner says yes (golf 9, L127); only the
    // cook changed, the proposal stays as it was.
    dish_proposed_by:
      meal && (meal.dish ?? null) === menu.dish && (meal.recipe_id ?? null) === menu.recipeId
        ? (meal.dish_proposed_by ?? null)
        : meal && meal.user_id !== user?.user.id && (menu.dish || menu.recipeId)
          ? (user?.user.id ?? null)
          : null,
  };
  await sync.mutate("meal.setMenu", { id, date, ...menu, at }, [id], async () => {
    if (meal) await db.meals.update(meal.id, values);
    else
      await db.meals.add({
        id,
        space_id: user?.settings?.personal_space_id ?? "",
        user_id: user?.user.id ?? "",
        meal_date: date,
        place: "home",
        ...values,
        version: 0,
        deleted_at: null,
        created_at: at,
        updated_at: at,
      });
  });
}

/** "Akkoord" on the dish the other one proposed. */
export async function acceptDish(meal: Meal): Promise<void> {
  await sync.mutate("meal.acceptDish", { id: meal.id }, [meal.id], async () => {
    await db.meals.update(meal.id, { dish_proposed_by: null });
  });
}

// ---- Samen doen (B47) ----

export function useSharedTodos(): SharedTodo[] | undefined {
  return useLiveQuery(async () => live(await db.shared_todos.toArray()).sort((a, b) => (a.created_at < b.created_at ? -1 : 1)));
}

export async function addTodo(title: string): Promise<void> {
  const id = uuidv7();
  const at = now();
  const user = await me();
  await sync.mutate("todo.add", { id, title: title.trim(), note: null, at }, [id], async () => {
    await db.shared_todos.add({
      id,
      space_id: user?.settings?.shared_space_id ?? "",
      title: title.trim(),
      note: null,
      done_at: null,
      created_by: user?.user.id ?? null,
      version: 0,
      deleted_at: null,
      created_at: at,
      updated_at: at,
    });
  });
}

export async function checkTodo(todo: SharedTodo, done: boolean): Promise<void> {
  const at = now();
  await sync.mutate("todo.check", { id: todo.id, done, at }, [todo.id], async () => {
    await db.shared_todos.update(todo.id, { done_at: done ? at : null });
  });
}

export async function removeTodo(todo: SharedTodo): Promise<void> {
  const at = now();
  await sync.mutate("todo.remove", { id: todo.id, at }, [todo.id], async () => {
    await db.shared_todos.update(todo.id, { deleted_at: at });
  });
}

export async function clearDoneTodos(): Promise<void> {
  const at = now();
  const done = live(await db.shared_todos.toArray()).filter((t) => t.done_at);
  await sync.mutate(
    "todo.clearDone",
    { at },
    done.map((t) => t.id),
    async () => {
      for (const t of done) await db.shared_todos.update(t.id, { deleted_at: at });
    },
  );
}

// ---- Notities ----

export function useNotes(): Note[] | undefined {
  return useLiveQuery(async () => live(await db.notes.toArray()).sort((a, b) => (a.updated_at < b.updated_at ? 1 : -1)));
}

export async function saveNote(id: string | null, title: string, body: string): Promise<string> {
  const noteId = id ?? uuidv7();
  const at = now();
  const user = await me();
  await sync.mutate("note.save", { id: noteId, title, body }, [noteId], async () => {
    if (id) await db.notes.update(id, { title, body, updated_at: at });
    else
      await db.notes.add({
        id: noteId,
        space_id: user?.settings?.personal_space_id ?? "",
        user_id: user?.user.id ?? "",
        title,
        body,
        version: 0,
        deleted_at: null,
        created_at: at,
        updated_at: at,
      });
  });
  return noteId;
}

export async function removeNote(id: string): Promise<void> {
  const at = now();
  await sync.mutate("note.remove", { id, at }, [id], async () => {
    await db.notes.update(id, { deleted_at: at });
  });
}

// ---- Wie is bezet ----

/** The other one's busy, away and shared times between two dates (B36, B42, B43). */
export function useOthersTime(from: string, to: string): Availability[] | undefined {
  return useLiveQuery(async () => {
    const user = await me();
    return live(await db.availability.where("avail_date").between(from, to, true, true).toArray())
      .filter((a) => a.owner_id !== user?.user.id)
      .sort((a, b) => (a.avail_date + (a.start_local ?? "") < b.avail_date + (b.start_local ?? "") ? -1 : 1));
  }, [from, to]);
}

/** Meer > Instellingen: Tsjits' times in the Week and the evening planning (B42); on by default. */
export function useShowPartner(): boolean | undefined {
  return useLiveQuery(async () => (await getMeta<boolean>("showPartner")) ?? true);
}

export async function setShowPartner(on: boolean): Promise<void> {
  await setMeta("showPartner", on);
  await sync.mutate("settings.setShowPartner", { on }, [], async () => {});
}

/** "Tsjits bezet 19:00 tot 21:00", "PK weg (Oudemirdum)", "Tsjits: Kapper 10:00". */
export function availabilityLine(a: Availability, name: string): string {
  const time = a.start_local
    ? `${Number(a.start_local.slice(0, 2))}:${a.start_local.slice(3, 5)} tot ${Number(a.end_local!.slice(0, 2))}:${a.end_local!.slice(3, 5)}`
    : "";
  if (a.kind === "away") return `${name} weg${a.title ? ` (${a.title})` : ""}`;
  if (a.kind === "busy") return `${name} bezet ${time}`;
  return `${name}: ${a.title ?? "afspraak"} ${time}`;
}

/** The name of the other account in the household. */
export function useHousehold() {
  return useLiveQuery(async () => (await me())?.household ?? []);
}
